import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { filter, tap, map, startWith } from 'rxjs/operators';

import { AppStateService } from '@app/core/services';
import { combineLatest, Observable } from 'rxjs';
import { SiloItem } from '@app/data/models';
import { untilDestroyed } from '@app/core/helpers/until-destroyed';

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss'],
    standalone: false,
})
export class UserDetailsComponent implements OnInit {
    @Input() canAccessSuperadminSection: boolean;
    @Input() hasAdminRights: boolean;
    @Input() userName: string;

    @Output() toggleProfileEvent = new EventEmitter<void>();
    @Output() signoutEvent = new EventEmitter<void>();

    isSiloSelectOpened: boolean;
    currentSiloControl: FormControl<number>;
    organisationManagementEnabled$ = inject(AppStateService).selectOrganisationManagementEnabled$;
    silos$ = inject(AppStateService).userSilos$;
    currentSilo$ = inject(AppStateService).currentSilo$;
    siloName$ = inject(AppStateService).siloName$;
    companyLogo$ = inject(AppStateService).logo$;
    private appStateService = inject(AppStateService);

    siloFilterControl = new FormControl('');
    filteredSilos$: Observable<SiloItem[]>;

    private untilDestroy = untilDestroyed();

    ngOnInit(): void {
        this.initSelectSiloForm();
        this.initFilteredSilos();
    }

    signout() {
        this.signoutEvent.emit();
    }

    toggleProfile() {
        this.toggleProfileEvent.emit();
    }

    selectSilo(siloId: number) {
        this.appStateService.switchUserSilo(siloId);
    }

    private initSelectSiloForm() {
        this.currentSilo$
            .pipe(
                this.untilDestroy(),
                filter((currentSilo) => !!currentSilo),
                tap((currentSilo) => (this.currentSiloControl = new FormControl(currentSilo.id))),
            )
            .subscribe();
    }

    onSiloSelectOpenedChange(isOpen: boolean) {
        this.isSiloSelectOpened = isOpen;
        if (!isOpen) {
            this.siloFilterControl.reset('');
        }
    }

    private initFilteredSilos() {
        this.filteredSilos$ = combineLatest([
            this.silos$,
            this.currentSilo$,
            this.siloFilterControl.valueChanges.pipe(startWith('')),
        ]).pipe(
            this.untilDestroy(),
            filter(([, currentSilo]) => !!currentSilo), // Add this line to filter out undefined currentSilo
            map(([silos, currentSilo, filterValue]) => {
                const lowercaseFilter = (filterValue || '').toLowerCase();
                return silos.filter(
                    (silo) =>
                        silo.id === currentSilo.id || // Always include the current silo
                        silo.name.toLowerCase().includes(lowercaseFilter),
                );
            }),
        );
    }
}
